import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { unwrap } from '@/api/envelope';
import {
  listCategories,
  listDocuments,
  readConfig,
  readDocumentActivistArcOrganizationPortalDocumentsDocumentIdGet as readDocument,
} from '@/api/generated';
import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';

import {
  firstScanPending,
  replacementPending,
  tabsFrom,
  toDocumentRow,
  type CategoryTab,
  type DocumentRow,
} from './documentLabels';
import { categoriesKey, configKey, detailKey, documentsKey, listKey } from './keys';
import { policyFrom, type UploadPolicy } from './uploadRules';
import { ALL_TAB, UNCATEGORIZED_TAB } from './documentLabels';
import type { DocumentsParams } from './useDocumentsParams';

/** Rows per page: the backend's default, and what the wireframe's pager implies. */
export const PAGE_SIZE = 20;

/** How often the list is asked again while a scan is pending (P10). */
export const POLL_MS = 10_000;

/**
 * Which organization's documents the page shows: the one staff are viewing,
 * or the signed-in person's own. Every key carries it (see keys.ts).
 */
export function useDocumentsOrganizationId(): string | null {
  const { user } = useAuth();
  const { viewing } = useViewingAs();
  return viewing ? viewing.id : (user?.organizationId ?? null);
}

/** Asks again for everything a write can change, for this organization only. */
export function useInvalidateDocuments(): () => Promise<void> {
  const queryClient = useQueryClient();
  const organizationId = useDocumentsOrganizationId();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: documentsKey(organizationId) }),
    [queryClient, organizationId],
  );
}

/** Whether any row is still being scanned, as a first file or a replacement (I4). */
export function anyPending(rows: readonly DocumentRow[]): boolean {
  return rows.some((row) => firstScanPending(row) || replacementPending(row));
}

/** The refetch interval for a list: every ten seconds while something is pending, else none. */
export function pollInterval(rows: readonly DocumentRow[] | undefined): number | false {
  return rows && anyPending(rows) ? POLL_MS : false;
}

export interface DocumentsPage {
  rows: DocumentRow[];
  tabs: CategoryTab[];
  pagination: { total: number; page: number; limit: number; hasMore: boolean };
}

/** The request for a view, with the defaults left out as the backend's own. */
export function listQueryFor(params: DocumentsParams) {
  const search = params.search.trim();
  return {
    page: params.page,
    limit: PAGE_SIZE,
    range: params.range,
    ...(search ? { search } : {}),
    ...(params.category === ALL_TAB
      ? {}
      : {
          category_id:
            params.category === UNCATEGORIZED_TAB ? 'uncategorized' : params.category,
        }),
    ...(params.placement ? { arc_placement: params.placement } : {}),
    ...(params.status ? { status: params.status } : {}),
  };
}

/**
 * One page of the library for a view. The previous page stays on screen
 * while the next loads, so the pager and the tabs keep their place, and the
 * query refetches on a timer only while a row is pending.
 */
export function useDocumentsList(params: DocumentsParams) {
  const organizationId = useDocumentsOrganizationId();
  return useQuery({
    queryKey: listKey(organizationId, params),
    enabled: organizationId !== null,
    queryFn: async (): Promise<DocumentsPage> => {
      const reply = await listDocuments({
        query: listQueryFor(params),
        throwOnError: true,
      });
      const data = unwrap(reply.data);
      return {
        rows: data.items.map(toDocumentRow),
        tabs: tabsFrom(data.category_counts),
        pagination: {
          total: data.pagination.total,
          page: data.pagination.page,
          limit: data.pagination.limit,
          hasMore: data.pagination.has_more,
        },
      };
    },
    placeholderData: keepPreviousData,
    refetchInterval: (query) => pollInterval(query.state.data?.rows),
    refetchIntervalInBackground: false,
  });
}

/** One document with its pending replacement, as the drawer and the scan check read it. */
export async function fetchDocument(id: string, range: number): Promise<DocumentRow> {
  const reply = await readDocument({
    path: { document_id: id },
    query: { range },
    throwOnError: true,
  });
  return toDocumentRow(unwrap(reply.data));
}

/** One document with its pending replacement, polled on the same rule. */
export function useDocumentDetail(id: string | null, range: number) {
  const organizationId = useDocumentsOrganizationId();
  return useQuery({
    queryKey: detailKey(organizationId, id ?? '', range),
    enabled: id !== null && organizationId !== null,
    queryFn: () => fetchDocument(id as string, range),
    refetchInterval: (query) => {
      const row = query.state.data;
      return row ? pollInterval([row]) : false;
    },
    refetchIntervalInBackground: false,
  });
}

/** The upload policy, read once per page and never hard-coded (P8). */
export function useUploadPolicy() {
  return useQuery({
    queryKey: configKey,
    queryFn: async (): Promise<UploadPolicy> => {
      const reply = await readConfig({ throwOnError: true });
      return policyFrom(unwrap(reply.data).documents);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface CategoryRow {
  /** Null for Uncategorized, which is not a row the backend keeps. */
  id: string | null;
  name: string;
  description: string | null;
  documentCount: number;
  isUncategorized: boolean;
}

export interface CategoriesPage {
  categories: CategoryRow[];
  uncategorized: CategoryRow;
  totals: { categories: number; documents: number; uncategorized: number };
}

/** The categories with their counts, the virtual Uncategorized row and the totals. */
export function useCategories() {
  const organizationId = useDocumentsOrganizationId();
  return useQuery({
    queryKey: categoriesKey(organizationId),
    enabled: organizationId !== null,
    queryFn: async (): Promise<CategoriesPage> => {
      const reply = await listCategories({ throwOnError: true });
      const data = unwrap(reply.data);
      const toRow = (
        row: (typeof data.categories)[number],
        isUncategorized = false,
      ): CategoryRow => ({
        id: row.id ?? null,
        name: row.name,
        description: row.description ?? null,
        documentCount: row.document_count,
        isUncategorized,
      });
      return {
        categories: data.categories.map((row) => toRow(row)),
        uncategorized: toRow(data.uncategorized, true),
        totals: data.totals,
      };
    },
  });
}
